import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SuperAdminContext, SuperAdminScope } from 'src/components';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { usePageHeader } from 'src/hooks/misc';
import OrganizationFilter from '../OrganizationFilter';
import { api, getApiErrorMessage } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';

type NotificationTemplateOverride = {
  template_id: number;
  org_id: number;
  type: string;
  subject: string;
  body: string;
  delivery_method: string;
  created_at?: string;
  last_modified?: string;
  created_by?: string;
  last_modified_by?: string;
  service_request_type_id?: number;
  condition_id?: number;
  twilio_messaging_service_sid?: string;
};

type Condition = {
  condition_id: number;
  condition_name: string;
};

type ServiceRequestType = {
  service_request_type_id: number;
  service_request_type: string;
};

type NotificationTemplateFormValues = {
  condition_id?: number;
  service_request_type_id?: number;
  type?: string;
  delivery_method?: string;
  subject?: string;
  body?: string;
  twilio_messaging_service_sid?: string;
};

type NotificationTemplateRequestPayload = {
  condition_id?: number;
  service_request_type_id?: number;
  type?: string;
  delivery_method?: string;
  subject?: string;
  body?: string;
  twilio_messaging_service_sid?: string;
};

const DELIVERY_METHOD_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'SMS', value: 'sms' },
];

const NotificationTemplateOverridesContent = () => {
  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<NotificationTemplateFormValues>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] =
    useState<NotificationTemplateOverride | null>(null);
  const [filters, setFilters] = useState<{
    conditionId?: number;
    serviceRequestTypeId?: number;
  }>({});

  const headers = useMemo(
    () => ({
      ...(superAdminHeaders || {}),
    }),
    [superAdminHeaders]
  );
  const deliveryMethodWatch = Form.useWatch('delivery_method', form);
  const conditionWatch = Form.useWatch('condition_id', form);
  const serviceRequestTypeWatch = Form.useWatch(
    'service_request_type_id',
    form
  );

  usePageHeader({
    title: 'Notification template overrides',
  });

  const { data: conditions = [], isLoading: isLoadingConditions } = useQuery({
    queryKey: ['superadmin-conditions'],
    queryFn: () =>
      api
        .get<{ conditions?: Condition[] }>('/get_all_conditions', { headers })
        .then((res) => res.data?.conditions || []),
  });

  const {
    data: serviceRequestTypes = [],
    isLoading: isLoadingServiceRequests,
  } = useQuery({
    queryKey: ['superadmin-service-request-types'],
    queryFn: () =>
      api
        .get<{
          data?: ServiceRequestType[];
        }>('/v3/service_request/types', { headers })
        .then((res) => res.data?.data || []),
  });

  const {
    data: notificationTemplateTypes = [],
    isLoading: isLoadingTemplateTypes,
  } = useQuery({
    queryKey: ['notification-template-types'],
    queryFn: () =>
      api
        .get<{
          notification_template_types?: string[];
        }>('/v2/notification_template_types', { headers })
        .then((res) => res.data?.notification_template_types || []),
  });

  const {
    data: overrides = [],
    isLoading: isLoadingOverrides,
    isFetching: isFetchingOverrides,
  } = useQuery({
    queryKey: [
      'notification-template-overrides',
      orgUuid,
      filters.conditionId,
      filters.serviceRequestTypeId,
    ],
    enabled: !!orgUuid,
    queryFn: () => {
      const params: Record<string, number> = {};
      if (filters.conditionId) {
        params.condition_id = filters.conditionId;
      } else if (filters.serviceRequestTypeId) {
        params.service_request_type_id = filters.serviceRequestTypeId;
      }

      return api
        .get<{
          notification_template_overrides?: NotificationTemplateOverride[];
        }>('/v2/notification_template_overrides', { headers, params })
        .then((res) => res.data?.notification_template_overrides || []);
    },
  });

  const refetchOverrides = () =>
    queryClient.invalidateQueries({
      queryKey: ['notification-template-overrides'],
    });

  const createOverrideMutation = useMutation({
    mutationFn: (payload: NotificationTemplateRequestPayload) =>
      api.post('/v2/notification_template_overrides', payload, { headers }),
    onSuccess: () => {
      showToastSuccess('Notification template override created successfully');
      setIsModalOpen(false);
      form.resetFields();
      refetchOverrides();
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) ||
          'Failed to create notification template override'
      );
    },
  });

  const updateOverrideMutation = useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: number;
      payload: NotificationTemplateRequestPayload;
    }) =>
      api.put(
        '/v2/notification_template_overrides',
        { template_id: templateId, ...payload },
        { headers }
      ),
    onSuccess: () => {
      showToastSuccess('Notification template override updated successfully');
      setIsModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      refetchOverrides();
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) ||
          'Failed to update notification template override'
      );
    },
  });

  const isSaving =
    createOverrideMutation.isPending || updateOverrideMutation.isPending;

  const conditionOptions = useMemo(
    () =>
      conditions.map((condition) => ({
        value: condition.condition_id,
        label: condition.condition_name,
      })),
    [conditions]
  );

  const conditionLookup = useMemo(() => {
    const map = new Map<number, string>();
    conditionOptions.forEach((option) => map.set(option.value, option.label));
    return map;
  }, [conditionOptions]);

  const serviceRequestOptions = useMemo(
    () =>
      serviceRequestTypes.map((type) => ({
        value: type.service_request_type_id,
        label: type.service_request_type,
      })),
    [serviceRequestTypes]
  );

  const serviceRequestLookup = useMemo(() => {
    const map = new Map<number, string>();
    serviceRequestOptions.forEach((option) =>
      map.set(option.value, option.label)
    );
    return map;
  }, [serviceRequestOptions]);

  const openCreateModal = () => {
    setEditingRecord(null);
    setIsModalOpen(true);
    form.setFieldsValue({
      delivery_method: 'email',
      condition_id: undefined,
      service_request_type_id: undefined,
    });
  };

  const openEditModal = (record: NotificationTemplateOverride) => {
    setEditingRecord(record);
    setIsModalOpen(true);
    form.setFieldsValue({
      condition_id: record.condition_id,
      service_request_type_id: record.service_request_type_id,
      type: record.type,
      delivery_method: record.delivery_method,
      subject: record.subject,
      body: record.body,
      twilio_messaging_service_sid: record.twilio_messaging_service_sid,
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const hasCondition = values.condition_id != null;
    const hasServiceRequestType = values.service_request_type_id != null;

    if (hasCondition && hasServiceRequestType) {
      showToastError(
        'Select either a condition or a service request type, not both.'
      );
      return;
    }

    if (!hasCondition && !hasServiceRequestType) {
      showToastError('Select a condition or a service request type.');
      return;
    }

    const payload: NotificationTemplateRequestPayload = {
      condition_id: values.condition_id,
      service_request_type_id: values.service_request_type_id,
      type: values.type?.trim(),
      delivery_method: values.delivery_method,
      subject: values.subject?.trim(),
      body: values.body?.trim(),
      twilio_messaging_service_sid: values.twilio_messaging_service_sid?.trim()
        ? values.twilio_messaging_service_sid.trim()
        : undefined,
    };

    if (editingRecord) {
      updateOverrideMutation.mutate({
        templateId: editingRecord.template_id,
        payload,
      });
    } else {
      createOverrideMutation.mutate(payload);
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields();
    }
  }, [form, isModalOpen]);

  const renderDate = (value?: string) => {
    if (!value) return 'N/A';
    const parsedDate = dayjs(value);
    return parsedDate.isValid()
      ? parsedDate.format('MMM D, YYYY h:mm A')
      : value;
  };

  const columns: ColumnsType<NotificationTemplateOverride> = [
    {
      title: 'Condition',
      dataIndex: 'condition_id',
      key: 'condition',
      render: (_, record) =>
        record.condition_id
          ? conditionLookup.get(record.condition_id) || record.condition_id
          : 'N/A',
    },
    {
      title: 'Service request type',
      dataIndex: 'service_request_type_id',
      key: 'service_request_type_id',
      render: (_, record) =>
        record.service_request_type_id
          ? serviceRequestLookup.get(record.service_request_type_id) ||
            record.service_request_type_id
          : 'N/A',
    },
    {
      title: 'Notification type',
      dataIndex: 'type',
      key: 'type',
      render: (value) => value || 'N/A',
    },
    {
      title: 'Delivery',
      dataIndex: 'delivery_method',
      key: 'delivery_method',
      render: (value) =>
        value ? (
          <Tag color={value === 'sms' ? 'blue' : 'green'}>
            {value.toUpperCase()}
          </Tag>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      render: (value) => (
        <Typography.Text ellipsis style={{ maxWidth: 240 }}>
          {value || 'N/A'}
        </Typography.Text>
      ),
    },
    {
      title: 'Last modified',
      dataIndex: 'last_modified',
      key: 'last_modified',
      render: (value) => renderDate(value),
    },
    {
      title: 'Updated by',
      dataIndex: 'last_modified_by',
      key: 'last_modified_by',
      render: (value) => value || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" onClick={() => openEditModal(record)}>
          Edit
        </Button>
      ),
    },
  ];

  const hasOrgSelected = Boolean(orgUuid);

  return (
    <div className="page-wrapper">
      <OrganizationFilter />

      {!hasOrgSelected && (
        <div className="mt-4">
          <Alert
            message="Select an organization to manage overrides."
            type="info"
            showIcon
          />
        </div>
      )}

      <div className="flex flex-col gap-4 mt-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <Space wrap>
            <Select
              allowClear
              showSearch
              style={{ minWidth: 240 }}
              placeholder="Filter by condition"
              options={conditionOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              loading={isLoadingConditions}
              value={filters.conditionId}
              onChange={(value) =>
                setFilters({
                  conditionId: value as number | undefined,
                  serviceRequestTypeId: undefined,
                })
              }
            />
            <Select
              allowClear
              showSearch
              style={{ minWidth: 260 }}
              placeholder="Filter by service request type"
              options={serviceRequestOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              loading={isLoadingServiceRequests}
              value={filters.serviceRequestTypeId}
              onChange={(value) =>
                setFilters({
                  conditionId: undefined,
                  serviceRequestTypeId: value as number | undefined,
                })
              }
            />
          </Space>

          <Button
            type="primary"
            disabled={!hasOrgSelected}
            onClick={openCreateModal}
          >
            Add override
          </Button>
        </div>

        <Table
          bordered
          rowKey="template_id"
          loading={isLoadingOverrides || isFetchingOverrides}
          dataSource={overrides}
          columns={columns}
          pagination={{ pageSize: 10 }}
        />
      </div>

      <Modal
        title={
          editingRecord
            ? 'Edit notification template override'
            : 'Add notification template override'
        }
        width="50vw"
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText={editingRecord ? 'Save changes' : 'Create override'}
        confirmLoading={isSaving}
        destroyOnClose
      >
        <Form
          layout="vertical"
          style={{ marginTop: 16 }}
          form={form}
          initialValues={{
            delivery_method: 'email',
          }}
        >
          <Form.Item label="Condition" name="condition_id">
            <Select
              allowClear
              showSearch
              options={conditionOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              placeholder="Select a condition"
              disabled={Boolean(serviceRequestTypeWatch)}
              onChange={(value) =>
                form.setFieldsValue({
                  condition_id: value as number | undefined,
                  service_request_type_id: undefined,
                })
              }
            />
          </Form.Item>

          <Form.Item
            label="Service request type"
            name="service_request_type_id"
          >
            <Select
              allowClear
              showSearch
              options={serviceRequestOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              placeholder="Select a service request type"
              disabled={Boolean(conditionWatch)}
              onChange={(value) =>
                form.setFieldsValue({
                  service_request_type_id: value as number | undefined,
                  condition_id: undefined,
                })
              }
            />
          </Form.Item>

          <Form.Item
            label="Notification type"
            name="type"
            rules={[
              { required: true, message: 'Notification type is required' },
            ]}
          >
            <Select
              showSearch
              loading={isLoadingTemplateTypes}
              optionFilterProp="label"
              placeholder="Select notification type"
              options={notificationTemplateTypes.map((templateType) => ({
                label: templateType,
                value: templateType,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Delivery method"
            name="delivery_method"
            rules={[{ required: true, message: 'Delivery method is required' }]}
          >
            <Select options={DELIVERY_METHOD_OPTIONS} />
          </Form.Item>

          <Form.Item
            label="Subject"
            required={deliveryMethodWatch !== 'sms'}
            name="subject"
            rules={[
              {
                validator: async (_, value) => {
                  const deliveryMethod = form.getFieldValue('delivery_method');
                  if (deliveryMethod === 'sms') return Promise.resolve();
                  if (!value || !value.trim()) {
                    return Promise.reject(
                      new Error('Subject is required for email')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Subject" />
          </Form.Item>

          <Form.Item
            label="Body"
            name="body"
            rules={[
              {
                validator: async (_, value) => {
                  if (value && value.trim()) return Promise.resolve();
                  return Promise.reject(new Error('Body is required'));
                },
              },
            ]}
          >
            <Input.TextArea rows={6} placeholder="Notification body" />
          </Form.Item>

          <Form.Item
            label="Twilio messaging service SID"
            name="twilio_messaging_service_sid"
          >
            <Input placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const NotificationTemplateOverridesPage = () => (
  <SuperAdminScope>
    <NotificationTemplateOverridesContent />
  </SuperAdminScope>
);

export default NotificationTemplateOverridesPage;
